import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AsyncJob, jobStatusEnum } from '../services/async-jobs/models'
import { createSelectors } from './utils'

const MAX_RECENT = 20

type AsyncJobState = {
  active: AsyncJob[]
  recent: AsyncJob[]
  acknowledgedFailures: string[]
  lastSyncedAt: string | null
  actions: {
    replaceActive: (jobs: AsyncJob[]) => void
    pushRecent: (job: AsyncJob) => void
    acknowledgeJob: (id: string) => void
    clearRecent: () => void
    removeFromActive: (id: string) => void
    restoreActive: (job: AsyncJob) => void
  }
}

const defaultValues: Omit<AsyncJobState, 'actions'> = {
  active: [],
  recent: [],
  acknowledgedFailures: [],
  lastSyncedAt: null,
}

export const useAsyncJobStore = createSelectors(
  create<AsyncJobState>()(
    persist(
      (set) => ({
        ...defaultValues,
        actions: {
          replaceActive(jobs) {
            set({ active: jobs, lastSyncedAt: new Date().toISOString() })
          },
          pushRecent(job) {
            set((state) => {
              const newRecent = [job, ...state.recent].slice(0, MAX_RECENT)
              const recentIds = new Set(newRecent.map((j) => j.id))
              const prunedAcknowledged = state.acknowledgedFailures.filter((id) =>
                recentIds.has(id),
              )
              return { recent: newRecent, acknowledgedFailures: prunedAcknowledged }
            })
          },
          acknowledgeJob(id) {
            set((state) => {
              if (state.acknowledgedFailures.includes(id)) return state
              return { acknowledgedFailures: [...state.acknowledgedFailures, id] }
            })
          },
          clearRecent() {
            set({ recent: [], acknowledgedFailures: [] })
          },
          removeFromActive(id) {
            set((state) => ({
              active: state.active.filter((j) => j.id !== id),
            }))
          },
          restoreActive(job) {
            set((state) => {
              if (state.active.some((j) => j.id === job.id)) return state
              // A fast job's terminal event can land before the dispatch response that
              // adds it optimistically; re-adding it would leave it "active" for good.
              if (state.recent.some((j) => j.id === job.id)) return state
              return { active: [...state.active, job] }
            })
          },
        },
      }),
      {
        name: 'async-jobs',
        version: 1,
        partialize(state) {
          const { actions: _, ...rest } = state
          return rest
        },
        merge(persisted, current) {
          const p = persisted as Partial<Omit<AsyncJobState, 'actions'>> | null
          return {
            ...current,
            active: Array.isArray(p?.active) ? p.active : [],
            recent: Array.isArray(p?.recent) ? p.recent : [],
            acknowledgedFailures: Array.isArray(p?.acknowledgedFailures)
              ? p.acknowledgedFailures
              : [],
            lastSyncedAt: p?.lastSyncedAt ?? null,
          }
        },
      },
    ),
  ),
)

// Selector hooks

export function useIsJobActive(taskType: string, entityId: string): boolean {
  return useAsyncJobStore((s) =>
    s.active.some((j) => j.taskType === taskType && j.entityId === entityId),
  )
}

export function useActiveJobsByType(taskType: string): AsyncJob[] {
  return useAsyncJobStore((s) => s.active.filter((j) => j.taskType === taskType))
}

export function useRecentJob(taskType: string, entityId: string): AsyncJob | undefined {
  return useAsyncJobStore((s) =>
    s.recent.find((j) => j.taskType === taskType && j.entityId === entityId),
  )
}

export function useActiveJobCount(): number {
  return useAsyncJobStore((s) => s.active.length)
}

export function useUnacknowledgedFailures(): AsyncJob[] {
  return useAsyncJobStore((s) => {
    const ackSet = new Set(s.acknowledgedFailures)
    return s.recent.filter((j) => j.status === jobStatusEnum.FAILED && !ackSet.has(j.id))
  })
}

export function useUnacknowledgedFailureCount(): number {
  return useAsyncJobStore((s) => {
    const ackSet = new Set(s.acknowledgedFailures)
    return s.recent.filter((j) => j.status === jobStatusEnum.FAILED && !ackSet.has(j.id)).length
  })
}
